import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Navbar, Footer } from '../components/landing';

import ReactMarkdown from 'react-markdown';
import { privacyPolicyContent, termsOfServiceContent, riskDisclosureContent } from './legal.content';
import { featuresContent, aboutUsContent } from './marketing.content';

// Define a simple mapping of page IDs to human-readable titles,
// and optionally map to specific content blocks if they were available
const pageDataMap = {
  // Product
  features: { title: "Features", description: "Discover the powerful algorithmic trading features of StockAlgoTrader.", content: featuresContent },
  pricing: { title: "Pricing", description: "Transparent pricing plans tailored for traders of all volumes." },
  backtesting: { title: "Backtesting Engine", description: "Test your strategies on historical data before going live." },
  api: { title: "API Integrations", description: "Seamlessly connect with your favorite brokers using our robust API." },
  
  // Resources
  docs: { title: "Documentation", description: "Comprehensive guides and API references to get you started." },
  blog: { title: "Blog", description: "Insights, updates, and trading tips from the StockAlgoTrader team." },
  tutorials: { title: "Tutorials", description: "Step-by-step video tutorials and walkthroughs." },
  support: { title: "Support Center", description: "We are here to help. Find answers to common questions.", content: "Write email to [support@stockalgotrader.com](mailto:support@stockalgotrader.com)" },
  
  // Company
  about: { title: "About Us", description: "Learn more about our mission to democratize algorithmic trading.", content: aboutUsContent },
  careers: { title: "Careers", description: "Join our team and help build the future of trading technology." },
  contact: { title: "Contact Us", description: "Get in touch with our team for business inquiries and partnerships.", content: "Write email to [support@stockalgotrader.com](mailto:support@stockalgotrader.com)" },
  partners: { title: "Partners", description: "Explore our network of trusted broker and data partners." },
  
  // Legal
  privacy: { title: "Privacy Policy", description: "How we collect, use, and protect your personal information.", content: privacyPolicyContent },
  terms: { title: "Terms of Service", description: "The rules and guidelines for using StockAlgoTrader.", content: termsOfServiceContent },
  risk: { title: "Risk Disclosure", description: "Important information regarding the risks of algorithmic trading.", content: riskDisclosureContent },
  sebi: { title: "SEBI Disclaimer", description: "Regulatory disclaimers regarding SEBI compliance." }
};

const PublicInfoPage = () => {
  const { pageId } = useParams();
  
  const pageInfo = pageDataMap[pageId] || {
    title: "Information",
    description: "Information page."
  };

  useEffect(() => {
    document.title = `${pageInfo.title} - StockAlgoTrader`;
    // Scroll to top when this dynamically routed page mounts
    window.scrollTo(0, 0);
  }, [pageInfo.title]);

  return (
    <div className="min-h-screen bg-base-100 flex flex-col font-sans">
      <Navbar />
      
      <main className="flex-grow w-full bg-base-200 px-4 md:px-12 lg:px-20 py-12 sm:py-20 border-b border-base-300">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-heading text-base-content">{pageInfo.title}</h1>
          
          <div className="prose prose-lg max-w-none text-base-content/80">
            <p className="text-xl md:text-2xl mb-12 font-medium bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              {pageInfo.description}
            </p>
            
            {pageInfo.content ? (
              <div className="bg-base-100 p-8 md:p-10 rounded-xl border border-base-300">
                <ReactMarkdown
                  components={{
                    h1: ({node, ...props}) => <h1 className="text-4xl font-bold mt-8 mb-4" {...props} />,
                    h2: ({node, ...props}) => <h2 className="text-3xl font-semibold mt-8 mb-4 border-b border-base-200 pb-2" {...props} />,
                    h3: ({node, ...props}) => <h3 className="text-2xl font-semibold mt-6 mb-3" {...props} />,
                    h4: ({node, ...props}) => <h4 className="text-xl font-medium mt-4 mb-2" {...props} />,
                    p: ({node, ...props}) => <p className="mb-4 leading-relaxed" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />,
                    ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />,
                    li: ({node, ...props}) => <li className="" {...props} />,
                    a: ({node, ...props}) => <a className="text-primary hover:underline font-medium" {...props} />,
                    strong: ({node, ...props}) => <strong className="font-semibold" {...props} />,
                    img: ({node, ...props}) => <img className="w-full rounded-2xl shadow-xl mt-6 mb-10 border border-base-300" {...props} />
                  }}
                >
                  {pageInfo.content}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="bg-base-100/50 p-8 rounded-xl border border-base-300 backdrop-blur-sm">
                <p className="text-center italic opacity-70">
                  [ Content for <strong>{pageInfo.title}</strong> is currently under development. Please check back later. ]
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PublicInfoPage;
